import { HUMAN, type Task } from "@tasks/tasksClient";
import type { View } from "@views/viewsClient";

/**
 * The one thing the subject pane's reader is showing (WEB-3288). Room-peer rows
 * aren't here: those navigate to another patient rather than selecting.
 */
export type PaneSelection =
  | { kind: "view"; id: string }
  | { kind: "task"; id: string };

/** Whether two selections point at the same rail row (for the selected style). */
export const isSameSelection = (
  a: PaneSelection | null,
  b: PaneSelection,
): boolean => !!a && a.kind === b.kind && a.id === b.id;

/**
 * What the reader opens on: the first view (the list arrives surfaced-first,
 * BE-1909), else the first task, else `null` for the empty state. `human` to-dos
 * are skipped — they're read and resolved from their rail row, so opening one
 * would just duplicate it.
 */
export const defaultSelection = ({
  views,
  tasks,
}: {
  views: View[];
  tasks: Task[];
}): PaneSelection | null => {
  const view = views[0];
  if (view) return { kind: "view", id: view.id };
  const task = tasks.find((candidate) => candidate.kind !== HUMAN);
  if (task) return { kind: "task", id: task.id };
  return null;
};

/**
 * The user's pick while it still exists, else the default — so re-curation can't
 * swap out what's being read, and a deleted view or resolved task can't strand
 * the reader.
 */
export const resolveSelection = ({
  picked,
  views,
  tasks,
}: {
  picked: PaneSelection | null;
  views: View[];
  tasks: Task[];
}): PaneSelection | null => {
  const fallback = () => defaultSelection({ views, tasks });
  if (!picked) return fallback();

  const exists =
    picked.kind === "view"
      ? views.some((view) => view.id === picked.id)
      : tasks.some((task) => task.id === picked.id);
  return exists ? picked : fallback();
};
